import React from 'react';
import { Form, Input, Select, Tabs, Tooltip, Card, Button, Space } from 'antd';

import { SuperGroup } from '@services/types';

import styles from './create-api-user-modal.module.scss';
import { selectSearchFilter, validatePassword } from '@services/utils';
import { toast } from 'react-toastify';
import api from '@services/api';
import { ApiUserRequest, ApiUserResponse } from '@openapi';
import { ArrowLeftOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useHistory, useParams } from 'react-router-dom';
import { Navigation } from '@constants/Navigation';
import { getPasswordRequirementsTooltip } from '@services/passwordRecommendation';
import { AppContext } from '@layouts/AppRouter';

const { TabPane } = Tabs;

const CreateApiUserModal = () => {
  const { variablesProfile } = React.useContext(AppContext);
  const [form] = Form.useForm<Omit<ApiUserRequest, 'id'>>();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const [superGroups, setSuperGroups] = React.useState<SuperGroup[]>([]);
  const [apiUser, setApiUser] = React.useState<ApiUserResponse | any>();

  const onClose = () => {
    history.push(Navigation.ApiUsers);
  };

  React.useEffect(() => {
    if (id) {
      api.administration.getApiUserById(id).then(setApiUser);
    }
  }, []);

  React.useEffect(() => {
    api.administration.getSuperGroups().then(setSuperGroups);
  }, []);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (id) {
        await api.administration.updateApiUser({ ...values, id: id });
        toast.success('ApiUser was updated successfully');
      } else {
        await api.administration.createApiUser(values);
        toast.success('ApiUser was created successfully');
      }
      form.resetFields();
      onClose();
    } catch (error) {
      toast.error('ApiUser was not updated/created due to error');
      console.log('CreateApiUserModal ~ handleSubmit ~ error', error);
    }
  };

  return apiUser ? (
    <Card
      title={apiUser ? 'Update an Api User' : 'Create a new apiUser'}
      extra={
        <Space>
          <Button icon={<ArrowLeftOutlined />} type="default" onClick={onClose}>
            Back to Api Users Page
          </Button>
          <Button type="primary" onClick={handleSubmit}>
            {apiUser ? 'Update' : 'Create'}
          </Button>
        </Space>
      }
    >
      <Tabs type="card" size="large" defaultActiveKey={'info' || 'security'}>
        <TabPane tab=" Update Info" key="info">
          <Form
            form={form}
            layout="vertical"
            name="form_in_modal"
            className={styles.form}
            initialValues={{
              description: apiUser?.description || '',
              apiKey: apiUser?.apiKey || '',
              role: apiUser?.roles[0] || '',
              superGroupIds: apiUser?.etc.perimeter || [],
            }}
          >
            {apiUser && (
              <Form.Item name="apiKey" style={{ gridArea: 'apiKey' }} label="Api User Key">
                <Input disabled={!!apiUser} />
              </Form.Item>
            )}
            <Form.Item
              name="description"
              style={{ gridArea: 'description' }}
              label="Description"
              rules={[
                {
                  required: true,
                  message: 'Please input the description of apiUser!',
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="role"
              style={{ gridArea: 'role' }}
              label="Role"
              rules={[
                {
                  required: true,
                  message: 'Please input the role of apiUser!',
                },
              ]}
            >
              <Select allowClear showSearch filterOption={selectSearchFilter}>
                <Select.Option value="AUTOMATION_INTEGRATION">Automation Integration</Select.Option>
                <Select.Option value="AUTOMATION_QUALITY">Automation Quality</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="superGroupIds"
              style={{ gridArea: 'supergroups' }}
              label="Scope"
              rules={[
                {
                  required: true,
                  message: 'Please select the scope of ApiUser!',
                },
              ]}
            >
              <Select mode="multiple" allowClear showSearch filterOption={selectSearchFilter}>
                {superGroups.map((item) => (
                  <Select.Option key={item.id} value={item.id}>
                    {item.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        </TabPane>
        <TabPane tab="Security" key="security">
          <Form
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridGap: '35px' }}
            form={form}
            layout="vertical"
            name="form_in_modal"
            initialValues={{
              password: '',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr max-content',
                gridGap: '10px',
                alignItems: 'center',
              }}
            >
              <Form.Item
                name="password"
                label="New Password"
                rules={[
                  {
                    required: true,
                    message: 'Please input your password!',
                  },
                  () => ({
                    validator(_, value) {
                      const error = validatePassword(value, variablesProfile?.minPasswordSize);
                      if (error) {
                        return Promise.reject(new Error(error));
                      } else {
                        return Promise.resolve();
                      }
                    },
                  }),
                ]}
                hasFeedback
              >
                <Input.Password />
              </Form.Item>
              <Tooltip placement="right" title={getPasswordRequirementsTooltip(variablesProfile?.minPasswordSize)}>
                <InfoCircleOutlined />
              </Tooltip>
            </div>
            <Form.Item
              name="confirm"
              label="Confirm Password"
              dependencies={['password']}
              hasFeedback
              rules={[
                {
                  required: true,
                  message: 'Please confirm your password!',
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }

                    return Promise.reject(new Error('The two passwords that you entered do not match!'));
                  },
                }),
              ]}
            >
              <Input.Password />
            </Form.Item>
          </Form>
        </TabPane>
      </Tabs>
    </Card>
  ) : !id ? (
    <Card
      title="Create a new apiUser"
      extra={
        <Space>
          <Button icon={<ArrowLeftOutlined />} type="default" onClick={onClose}>
            Back to Api Users Page
          </Button>
          <Button type="primary" onClick={handleSubmit}>
            Create
          </Button>
        </Space>
      }
    >
      <Tabs type="card" size="large" defaultActiveKey={'info' || 'security'}>
        <TabPane tab="Add Info" key="info">
          <Form
            form={form}
            layout="vertical"
            name="form_in_modal"
            className={styles.form}
            initialValues={{
              description: '',
              apiKey: '',
              role: '',
              superGroupIds: [],
            }}
          >
            <Form.Item
              name="description"
              style={{ gridArea: 'description' }}
              label="Description"
              rules={[
                {
                  required: true,
                  message: 'Please input the description of apiUser!',
                },
              ]}
            >
              <Input />
            </Form.Item>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gridGap: '35px',
                gridArea: 'password',
              }}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr max-content',
                  gridGap: '10px',
                  alignItems: 'center',
                }}
              >
                <Form.Item
                  name="password"
                  label="New Password"
                  rules={[
                    {
                      required: true,
                      message: 'Please input your password!',
                    },
                    () => ({
                      validator(_, value) {
                        const error = validatePassword(value, variablesProfile?.minPasswordSize);
                        if (error) {
                          return Promise.reject(new Error(error));
                        } else {
                          return Promise.resolve();
                        }
                      },
                    }),
                  ]}
                  hasFeedback
                >
                  <Input.Password autoComplete="new-password" />
                </Form.Item>
                <Tooltip placement="right" title={getPasswordRequirementsTooltip(variablesProfile?.minPasswordSize)}>
                  <InfoCircleOutlined />
                </Tooltip>
              </div>

              <Form.Item
                name="confirm"
                label="Confirm Password"
                dependencies={['password']}
                hasFeedback
                rules={[
                  {
                    required: true,
                    message: 'Please confirm your password!',
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }

                      return Promise.reject(new Error('The two passwords that you entered do not match!'));
                    },
                  }),
                ]}
              >
                <Input.Password />
              </Form.Item>
            </div>
            <Form.Item
              name="role"
              style={{ gridArea: 'role' }}
              label="Role"
              rules={[
                {
                  required: true,
                  message: 'Please input the role of apiUser!',
                },
              ]}
            >
              <Select allowClear showSearch filterOption={selectSearchFilter}>
                <Select.Option value="AUTOMATION_INTEGRATION">Automation Integration</Select.Option>
                <Select.Option value="AUTOMATION_QUALITY">Automation Quality</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="superGroupIds"
              style={{ gridArea: 'supergroups' }}
              label="Scope"
              rules={[
                {
                  required: true,
                  message: 'Please select the scope of ApiUser!',
                },
              ]}
            >
              <Select mode="multiple" allowClear showSearch filterOption={selectSearchFilter}>
                {superGroups.map((item) => (
                  <Select.Option key={item.id} value={item.id}>
                    {item.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        </TabPane>
      </Tabs>
    </Card>
  ) : null;
};

export default React.memo(CreateApiUserModal);
